import * as assistantService from 'services/assistant';
import { getFacilityTree } from 'services/hotel';
import {
  FilterModel,
  getApplicableBedRules,
  getSearchRoomsString,
  H_TYPE_OPTIONS,
  HTypeCode,
  RoomType,
} from 'ts/hotel/classes/FilterModel';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import { FlightFilterModel, TimeFilter } from 'ts/flight/classes/FlightFilterModel';
import { getRoomTypesArr } from 'utils/hotel';

export type PageSearchContext = assistantService.PageSearchContext;
export type AssistantOpenPage = assistantService.AssistantOpenPage;

export const resolveAssistantOpenPage = (pathname: string): AssistantOpenPage | null => {
  if (/\/hotels?\d*\/results(\/|$)/i.test(pathname)) {
    return 'hotel-results';
  }
  if (/^\/proposal(\/|$)/i.test(pathname)) {
    return 'proposal';
  }
  if (/^\/flights(\/|$)/i.test(pathname)) {
    return 'flights';
  }
  if (/^\/my-orders/i.test(pathname)) {
    return 'my-orders';
  }
  if (/^\/hotels?\d*(\/|$)/i.test(pathname)) {
    return 'hotels';
  }
  return null;
};

export const buildPageSearchContext = (
  pathname: string,
  search: string,
): PageSearchContext | null => {
  if (!/\/hotels?\d*\/results(\/|$)/i.test(pathname)) {
    return null;
  }

  const params = new URLSearchParams(search);
  const city = params.get('city');
  const lat = params.get('lat');
  const lon = params.get('lon');
  const checkIn = params.get('checkIn');
  const checkOut = params.get('checkOut');
  if (!city || !lat || !lon || !checkIn || !checkOut) {
    return null;
  }

  const radiusRaw = params.get('searchRadius');
  const searchRadiusKm = radiusRaw ? Number.parseFloat(radiusRaw) : undefined;

  return {
    searchToken: sessionStorage.getItem('searchTokenHotels') || null,
    checkIn,
    checkOut,
    city,
    lat,
    lon,
    countryCode: params.get('id'),
    loc: params.get('loc'),
    roomsString: params.get('roomsString') || '2',
    nationality: params.get('nationality'),
    searchRadiusKm: Number.isFinite(searchRadiusKm) ? searchRadiusKm : null,
  };
};

// How long filters asked for together with a new search wait for that search to produce hotels.
export const PENDING_FILTERS_TTL_MS = 120_000;

// BePro staff: the branch whose users see the AI model badge and the Session Viewer. The backend enforces the same
// rule (AiAssistant:StaffBranchId) - it only sends the model to staff and refuses the viewer's endpoints to anyone
// else - so this only decides what is worth showing.
export const STAFF_BRANCH_ID = 5000;

// A search the assistant opened shows the loader until the page has hotels. If that never happens (no results, a failed
// search) the loader is not left spinning for ever.
export const SEARCH_LOADER_MAX_MS = 60_000;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  // The reply to a search that was opened on the page: shown as the loader while the page loads its results, then as
  // a short note. The text the backend sent with it ("Loading...") is never shown.
  search?: { loading: boolean };
  // The user's own voice message: the recording, kept in the page's memory so it can be played again while the page is
  // open. `text` is then the transcript: it is not written into the chat, it is only the bubble's hover text.
  audio?: { url: string; durationMs: number };
  // False for a reply the server wrote itself (a limit, "too long", "busy"): it is not stored, so it cannot be rated.
  feedbackAllowed?: boolean;
  // The rating this user sent for the answer (derived score + optional Yes/No answers), so the icon shows it and
  // the feedback window opens with it again.
  feedback?: {
    score: number;
    understood?: boolean | null;
    correct?: boolean | null;
    helped?: boolean | null;
    reason: string;
  };
}

// Shape of appliedFilters from the backend tools. Absent properties are left alone; empty arrays / null / "" / false
// clear that dimension on the page (how a reset reaches FilterModel).
export interface AppliedHotelFilters {
  stars?: number[] | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  refundableOnly?: boolean | null;
  breakfastIncluded?: boolean | null;
  mealPlans?: string[] | null;
  name?: string | null;
  roomName?: string | null;
  hotelTypes?: string[] | null;
  preferredOnly?: boolean | null;
  accessibleOnly?: boolean | null;
  pinOnly?: boolean | null;
  maxDistanceKm?: number | null;
  roomTypes?: string[] | null;
  bedTypes?: string[] | null;
  facilities?: string[] | null;
  suppliers?: string[] | null;
  sortBy?: string | null;
  displayMode?: number | string | null;
}

export const HOTEL_SORT_BY_VALUES = [
  'recommended',
  'priceIncrease',
  'priceDecrease',
  'starIncrease',
  'starDecrease',
  'nameIncrease',
  'nameDecrease',
  'distanceIncrease',
  'distanceDecrease',
] as const;

export const DISPLAY_MODE_ALIASES: Record<string, number> = {
  'agent-boarding': 7,
  'agent-boarding-category': 7,
  '7': 7,
  'agent-category': 13,
  'agent-category-type': 13,
  '13': 13,
  'agent-boarding-multi': 10,
  'agent-boarding-category-multi': 10,
  '10': 10,
  'agent-category-multi': 14,
  'agent-category-type-multi': 14,
  '14': 14,
  'business-boarding': 8,
  'business-boarding-category': 8,
  '8': 8,
  'business-category': 9,
  'business-category-type': 9,
  '9': 9,
  'business-boarding-multi': 11,
  'business-boarding-category-multi': 11,
  '11': 11,
  'business-category-multi': 12,
  'business-category-type-multi': 12,
  '12': 12,
  'agent-single': 15,
  '15': 15,
  'agent-single-multi': 17,
  '17': 17,
  'agent-single-2': 16,
  '16': 16,
};

export type AssistantFilterHighlightKind =
  | 'stars'
  | 'price'
  | 'location'
  | 'sort'
  | 'displayMode'
  | 'rooms'
  | 'suppliers'
  | 'facilities'
  | 'name'
  | 'meal'
  | 'additional'
  | 'stops'
  | 'baggage'
  | 'times'
  | 'duration'
  | 'airlines'
  | 'airports'
  | 'flightTypes';

export interface ApplyAssistantPageActions {
  onChangeFilter: (filter: FilterModel) => void;
  onSortChange?: (sort: string) => void;
  onDisplayModeChange?: (mode: number) => void;
  currentSortBy?: string;
  currentDisplayMode?: number;
}

export const resolveDisplayMode = (raw: number | string | null | undefined): number | null => {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Object.values(DISPLAY_MODE_ALIASES).includes(raw) ? raw : null;
  }
  const key = String(raw).trim().toLowerCase();
  return DISPLAY_MODE_ALIASES[key] ?? null;
};

const APPLIED_FILTER_KEYS: (keyof AppliedHotelFilters)[] = [
  'stars',
  'minPrice',
  'maxPrice',
  'refundableOnly',
  'breakfastIncluded',
  'mealPlans',
  'name',
  'roomName',
  'hotelTypes',
  'preferredOnly',
  'accessibleOnly',
  'pinOnly',
  'maxDistanceKm',
  'roomTypes',
  'bedTypes',
  'facilities',
  'suppliers',
  'sortBy',
  'displayMode',
];

const APPLIED_FILTER_KEY_ALIASES: Record<string, keyof AppliedHotelFilters> = {
  stars: 'stars',
  starrating: 'stars',
  star_rating: 'stars',
  minprice: 'minPrice',
  min_price: 'minPrice',
  maxprice: 'maxPrice',
  max_price: 'maxPrice',
  refundableonly: 'refundableOnly',
  refundable_only: 'refundableOnly',
  freecxl: 'refundableOnly',
  free_cxl: 'refundableOnly',
  breakfastincluded: 'breakfastIncluded',
  breakfast_included: 'breakfastIncluded',
  withmeal: 'breakfastIncluded',
  with_meal: 'breakfastIncluded',
  mealplans: 'mealPlans',
  meal_plans: 'mealPlans',
  name: 'name',
  roomname: 'roomName',
  room_name: 'roomName',
  hoteltypes: 'hotelTypes',
  hotel_types: 'hotelTypes',
  preferredonly: 'preferredOnly',
  preferred_only: 'preferredOnly',
  accessibleonly: 'accessibleOnly',
  accessible_only: 'accessibleOnly',
  pinonly: 'pinOnly',
  pin_only: 'pinOnly',
  maxdistancekm: 'maxDistanceKm',
  max_distance_km: 'maxDistanceKm',
  roomtypes: 'roomTypes',
  room_types: 'roomTypes',
  bedtypes: 'bedTypes',
  bed_types: 'bedTypes',
  facilities: 'facilities',
  amenities: 'facilities',
  amenity: 'facilities',
  petfriendly: 'facilities',
  pet_friendly: 'facilities',
  petsallowed: 'facilities',
  pets_allowed: 'facilities',
  pets: 'facilities',
  suppliers: 'suppliers',
  sortby: 'sortBy',
  sort_by: 'sortBy',
  displaymode: 'displayMode',
  display_mode: 'displayMode',
};

const FACILITY_SYNONYMS: Record<string, string[]> = {
  pet: ['pet', 'pets', 'pet friendly', 'pet-friendly', 'pets allowed', 'animals', 'dog', 'dogs'],
  pets: ['pet', 'pets', 'pet friendly', 'pet-friendly', 'pets allowed', 'animals', 'dog', 'dogs'],
  'pet friendly': ['pet', 'pets', 'pet friendly', 'pet-friendly', 'pets allowed', 'animals', 'dog'],
  'pet-friendly': ['pet', 'pets', 'pet friendly', 'pet-friendly', 'pets allowed', 'animals', 'dog'],
  'pets allowed': ['pet', 'pets', 'pet friendly', 'pet-friendly', 'pets allowed', 'animals', 'dog'],
};

// Facility tree id used by the facilities UI for pets (faDog).
const PET_FACILITY_IDS = new Set([37]);

const normalizeStars = (stars: unknown): number[] | null | undefined => {
  if (stars === null) {
    return null;
  }
  if (typeof stars === 'number' || typeof stars === 'string') {
    const rating = normalizeNumber(stars);
    if (typeof rating === 'number' && rating >= 3 && rating <= 5) {
      return Array.from({ length: 6 - rating }, (_, index) => rating + index);
    }
    return undefined;
  }
  if (!Array.isArray(stars)) {
    return undefined;
  }
  return stars
    .map((star) => (typeof star === 'number' ? star : Number(star)))
    .filter((star) => Number.isFinite(star));
};

const normalizeBoolean = (value: unknown): boolean | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === '0' || normalized === 'no') {
      return false;
    }
  }
  return undefined;
};

const normalizeNumber = (value: unknown): number | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const normalizeStringArray = (value: unknown): string[] | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return undefined;
  }
  if (typeof value === 'string') {
    const parts = value
      .split(/[,|;]/)
      .map((item) => item.trim())
      .filter(Boolean);
    return parts.length ? parts : undefined;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.map((item) => String(item).trim()).filter(Boolean);
};

const pickFilterSource = (raw: Record<string, unknown>) => {
  if (raw.appliedFilters && typeof raw.appliedFilters === 'object') {
    return raw.appliedFilters as Record<string, unknown>;
  }
  if (raw.filters && typeof raw.filters === 'object') {
    return raw.filters as Record<string, unknown>;
  }
  return raw;
};

// Accepts nested (`appliedFilters` / `filters`) or flat payloads, camelCase or snake_case, and string booleans.
export const parseAppliedHotelFilters = (
  raw: Record<string, unknown> | null | undefined,
): AppliedHotelFilters | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const source = pickFilterSource(raw);
  const filters: AppliedHotelFilters = {};
  let hasFilterKey = false;

  Object.entries(source).forEach(([key, value]) => {
    const normalizedKey = key.replace(/-/g, '_').toLowerCase();
    const mappedKey = APPLIED_FILTER_KEY_ALIASES[normalizedKey];
    if (!mappedKey) {
      return;
    }
    hasFilterKey = true;

    switch (mappedKey) {
      case 'stars':
        filters.stars = normalizeStars(value);
        break;
      case 'minPrice':
      case 'maxPrice':
      case 'maxDistanceKm':
        filters[mappedKey] = normalizeNumber(value);
        break;
      case 'refundableOnly':
      case 'breakfastIncluded':
      case 'preferredOnly':
      case 'accessibleOnly':
      case 'pinOnly':
        filters[mappedKey] = normalizeBoolean(value);
        break;
      case 'mealPlans':
      case 'hotelTypes':
      case 'roomTypes':
      case 'bedTypes':
      case 'suppliers':
        filters[mappedKey] = normalizeStringArray(value);
        break;
      case 'facilities': {
        const asBool = normalizeBoolean(value);
        if (typeof asBool === 'boolean') {
          if (asBool && /pet|dog|animal/.test(normalizedKey)) {
            filters.facilities = [...(filters.facilities || []), 'pet friendly'];
          }
          break;
        }
        const list = normalizeStringArray(value);
        if (list) {
          filters.facilities = [...(filters.facilities || []), ...list];
        } else if (/pet|dog|animal/.test(normalizedKey)) {
          filters.facilities = [...(filters.facilities || []), 'pet friendly'];
        }
        break;
      }
      case 'name':
      case 'roomName':
      case 'sortBy':
        filters[mappedKey] = value === null ? null : typeof value === 'string' ? value : String(value);
        break;
      case 'displayMode':
        filters.displayMode = value as AppliedHotelFilters['displayMode'];
        break;
      default:
        break;
    }
  });

  if (!hasFilterKey && APPLIED_FILTER_KEYS.some((key) => key in source)) {
    return { ...(source as AppliedHotelFilters) };
  }

  return hasFilterKey ? filters : null;
};

const SORT_BY_ALIASES: Record<string, (typeof HOTEL_SORT_BY_VALUES)[number]> = {
  recommended: 'recommended',
  priceincrease: 'priceIncrease',
  'price increase': 'priceIncrease',
  'price low': 'priceIncrease',
  'price low to high': 'priceIncrease',
  'low to high': 'priceIncrease',
  pricedecrease: 'priceDecrease',
  'price decrease': 'priceDecrease',
  'price high': 'priceDecrease',
  'price high to low': 'priceDecrease',
  'high to low': 'priceDecrease',
  starincrease: 'starIncrease',
  'star increase': 'starIncrease',
  stardecrease: 'starDecrease',
  'star decrease': 'starDecrease',
  nameincrease: 'nameIncrease',
  'name increase': 'nameIncrease',
  'name a-z': 'nameIncrease',
  'name az': 'nameIncrease',
  'a-z': 'nameIncrease',
  az: 'nameIncrease',
  namedecrease: 'nameDecrease',
  'name decrease': 'nameDecrease',
  'name z-a': 'nameDecrease',
  'name za': 'nameDecrease',
  'z-a': 'nameDecrease',
  za: 'nameDecrease',
  'z–a': 'nameDecrease',
  distanceincrease: 'distanceIncrease',
  'distance increase': 'distanceIncrease',
  'nearest first': 'distanceIncrease',
  distancedecrease: 'distanceDecrease',
  'distance decrease': 'distanceDecrease',
};

export const resolveSortBy = (raw: string | null | undefined): string | null => {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (SORT_BY_ALIASES[key]) {
    return SORT_BY_ALIASES[key];
  }
  const match = HOTEL_SORT_BY_VALUES.find((value) => value.toLowerCase() === key);
  return match ?? null;
};

const MEAL_PLAN_CODES = new Set(['BB', 'HB', 'FB', 'AI']);

const expandFacilityNeedles = (input: string): string[] => {
  const lower = input.trim().toLowerCase();
  if (!lower) {
    return [];
  }
  const synonyms = FACILITY_SYNONYMS[lower];
  if (synonyms) {
    return synonyms;
  }
  if (/pet|dog|animal/.test(lower)) {
    return FACILITY_SYNONYMS.pet;
  }
  return [lower];
};

const resolveFacilityCodes = (inputs: string[]): string[] => {
  const tree = getFacilityTree();
  const displayed = (tree?.Tree || []).flatMap((group) =>
    (group.subgroups || []).flatMap((subgroup) =>
      (subgroup.facilities || [])
        .filter((facility) => facility.display === 1)
        .map((facility) => ({
          facility,
          subgroupKey: `${group.group_code}-${subgroup.subgroup_code}`,
        })),
    ),
  );
  const codes = new Set<string>();

  inputs.forEach((input) => {
    const needle = input.trim();
    if (!needle) {
      return;
    }

    const byCode = displayed.find(
      ({ facility }) => facility.facility_code?.toLowerCase() === needle.toLowerCase(),
    );
    if (byCode) {
      codes.add(byCode.facility.facility_code);
      return;
    }

    const needles = expandFacilityNeedles(needle);
    const matchedSubgroups = new Set<string>();
    displayed.forEach(({ facility, subgroupKey }) => {
      const name = facility.name?.toLowerCase() || '';
      if (/\bno\b|\bnot\b|without/.test(name) && needles.some((item) => /pet|dog|animal/.test(item))) {
        return;
      }
      const code = facility.facility_code?.toLowerCase() || '';
      const matchesText = needles.some(
        (item) => name === item || name.includes(item) || code === item || code.includes(item),
      );
      const matchesPetId =
        PET_FACILITY_IDS.has(facility.facility_id) &&
        needles.some((item) => /pet|dog|animal/.test(item));
      if (matchesText || matchesPetId) {
        matchedSubgroups.add(subgroupKey);
      }
    });

    displayed.forEach(({ facility, subgroupKey }) => {
      if (matchedSubgroups.has(subgroupKey)) {
        codes.add(facility.facility_code);
      }
    });
  });

  return Array.from(codes);
};

const bedFilterFromInclude = (filter: FilterModel, include: string[]) => {
  const includeSet = new Set(include);
  const exclude = Array.from(
    new Set(
      getApplicableBedRules(filter.bedRules, getSearchRoomsString()).flatMap((group) =>
        (group.items || []).flatMap((rule) => {
          const isActive = (rule.positive || []).some((option) => includeSet.has(option));
          return isActive ? rule.negative || [] : [];
        }),
      ),
    ),
  );
  return { include, exclude };
};

const textMatchesNeedle = (text: string, needles: string[]) => {
  const hay = text.trim().toLowerCase();
  if (!hay) {
    return false;
  }
  return needles.some((needle) => hay === needle || hay.includes(needle) || needle.includes(hay));
};

const matchBedFilterOptions = (filter: FilterModel, needles: string[]) => {
  const rules = getApplicableBedRules(filter.bedRules, getSearchRoomsString())
    .flatMap((group) => group.items || [])
    .filter((rule) => !!rule.positive?.length);

  const matched: string[] = [];
  rules.forEach((rule) => {
    if (textMatchesNeedle(rule.title_lang || '', needles)) {
      matched.push(...(rule.positive || []));
      return;
    }
    (rule.positive || []).forEach((option) => {
      if (textMatchesNeedle(option, needles)) {
        matched.push(option);
      }
    });
  });

  return Array.from(new Set(matched));
};

const clearRoomTypesCheckboxes = (filter: FilterModel) => {
  const roomTypes = filter.roomTypes.map((group) => ({
    ...group,
    options: group.options.map((option) => ({ ...option, checked: false })),
  }));
  return {
    ...filter,
    roomTypes,
    roomTypesArr: getRoomTypesArr(roomTypes),
  };
};

const sameNumberList = (a: number[] = [], b: number[] = []) => {
  if (a.length !== b.length) {
    return false;
  }
  const left = [...a].sort((x, y) => x - y);
  const right = [...b].sort((x, y) => x - y);
  return left.every((value, index) => value === right[index]);
};

const sameStringList = (a: string[] = [], b: string[] = []) => {
  if (a.length !== b.length) {
    return false;
  }
  const left = [...a].map((value) => value.toLowerCase()).sort();
  const right = [...b].map((value) => value.toLowerCase()).sort();
  return left.every((value, index) => value === right[index]);
};

export const applyAssistantFilters = (
  filter: FilterModel,
  applied: AppliedHotelFilters | null | undefined,
  actions: ApplyAssistantPageActions,
): {
  changed: boolean;
  applied: AppliedHotelFilters | null;
  changedKinds: AssistantFilterHighlightKind[];
} => {
  if (!applied) {
    return { changed: false, applied: null, changedKinds: [] };
  }

  const { onChangeFilter, onSortChange, onDisplayModeChange, currentSortBy, currentDisplayMode } =
    actions;
  let next = filter;
  let changed = false;
  const changedKinds: AssistantFilterHighlightKind[] = [];
  const mark = (kind: AssistantFilterHighlightKind) => {
    if (!changedKinds.includes(kind)) {
      changedKinds.push(kind);
    }
    changed = true;
  };

  if (Array.isArray(applied.stars)) {
    const stars = applied.stars
      .map((star) => (typeof star === 'number' ? star : Number(star)))
      .filter((star) => Number.isFinite(star));
    if (!sameNumberList(filter.stars.value, stars)) {
      next = { ...next, stars: { ...next.stars, value: stars } };
      mark('stars');
    }
  }

  const hasMin = applied.minPrice === null || typeof applied.minPrice === 'number';
  const hasMax = applied.maxPrice === null || typeof applied.maxPrice === 'number';
  if (hasMin || hasMax) {
    const bounds = next.price;
    let [low, high] = bounds.value;
    if (applied.minPrice === null) {
      low = bounds.min;
    } else if (typeof applied.minPrice === 'number') {
      low = Math.max(bounds.min, Math.min(applied.minPrice, bounds.max || applied.minPrice));
    }
    if (applied.maxPrice === null) {
      high = bounds.max;
    } else if (typeof applied.maxPrice === 'number') {
      high = Math.max(low, Math.min(applied.maxPrice, bounds.max || applied.maxPrice));
    }
    if (low > high) {
      low = high;
    }
    if (low !== bounds.value[0] || high !== bounds.value[1]) {
      next = { ...next, price: { ...bounds, value: [low, high] } };
      mark('price');
    }
  }

  const refundableOnly = normalizeBoolean(applied.refundableOnly);
  if (typeof refundableOnly === 'boolean' && next.additional.freeCxl !== refundableOnly) {
    next = { ...next, additional: { ...next.additional, freeCxl: refundableOnly } };
    mark('additional');
  }

  const breakfastIncluded = normalizeBoolean(applied.breakfastIncluded);
  if (typeof breakfastIncluded === 'boolean') {
    const mealValue = next.meal.value || [];
    const nextMeal = breakfastIncluded
      ? Array.from(new Set([...mealValue, 'BB']))
      : mealValue.filter((code) => code !== 'BB');
    if (
      next.additional.withMeal !== breakfastIncluded ||
      !sameStringList(mealValue, nextMeal)
    ) {
      next = {
        ...next,
        additional: { ...next.additional, withMeal: breakfastIncluded },
        meal: { ...next.meal, value: nextMeal },
      };
      mark('meal');
    }
  }

  if (Array.isArray(applied.mealPlans)) {
    const value = Array.from(
      new Set(
        applied.mealPlans
          .map((code) => {
            const normalized = code.trim().toUpperCase();
            if (MEAL_PLAN_CODES.has(normalized)) {
              return normalized;
            }
            if (/BREAKFAST|ארוחת\s*בוקר|BB/.test(normalized) || /breakfast/i.test(code)) {
              return 'BB';
            }
            return '';
          })
          .filter((code) => MEAL_PLAN_CODES.has(code)),
      ),
    );
    if (!sameStringList(next.meal.value || [], value)) {
      next = {
        ...next,
        meal: { ...next.meal, value },
        additional: {
          ...next.additional,
          withMeal: value.includes('BB') ? true : next.additional.withMeal,
        },
      };
      mark('meal');
    }
  }

  if (typeof applied.name === 'string' && next.name !== applied.name) {
    next = { ...next, name: applied.name };
    mark('name');
  }

  if (typeof applied.roomName === 'string' && next.room !== applied.roomName) {
    next = { ...next, room: applied.roomName };
    mark('rooms');
  }

  if (Array.isArray(applied.hotelTypes) && next.hType) {
    const value = applied.hotelTypes
      .map((type) => type.trim().toUpperCase())
      .filter(
        (type): type is HTypeCode =>
          (H_TYPE_OPTIONS as readonly string[]).includes(type) &&
          next.hType!.options.includes(type),
      );
    if (!sameStringList(next.hType.value || [], value)) {
      next = { ...next, hType: { ...next.hType, value } };
      mark('rooms');
    }
  }

  if (
    typeof applied.preferredOnly === 'boolean' &&
    next.additional.preffered !== applied.preferredOnly
  ) {
    next = { ...next, additional: { ...next.additional, preffered: applied.preferredOnly } };
    mark('additional');
  }

  if (typeof applied.pinOnly === 'boolean' && next.pinOnly !== applied.pinOnly) {
    next = { ...next, pinOnly: applied.pinOnly };
    mark('additional');
  }

  const roomKindsProvided = Array.isArray(applied.roomTypes) || Array.isArray(applied.bedTypes);
  if (roomKindsProvided) {
    const beforeRoom = next.room;
    const beforeBed = JSON.stringify(next.bedFilter || {});
    const rawNeedles = [...(applied.roomTypes || []), ...(applied.bedTypes || [])]
      .map((name) => name.trim())
      .filter(Boolean);
    const needles = rawNeedles.map((name) => name.toLowerCase());

    if (!needles.length) {
      next = {
        ...clearRoomTypesCheckboxes(next),
        bedFilter: { include: [], exclude: [] },
      };
    } else {
      const bedMatches = matchBedFilterOptions(next, needles);
      if (bedMatches.length) {
        next = {
          ...clearRoomTypesCheckboxes(next),
          bedFilter: bedFilterFromInclude(next, bedMatches),
        };
      } else {
        next = {
          ...clearRoomTypesCheckboxes(next),
          bedFilter: { include: [], exclude: [] },
          room: rawNeedles.join('|'),
        };
      }
    }
    if (beforeRoom !== next.room || beforeBed !== JSON.stringify(next.bedFilter || {})) {
      mark('rooms');
    }
  }

  if (typeof applied.accessibleOnly === 'boolean') {
    if (next.additional.ada !== applied.accessibleOnly) {
      const roomTypes = next.roomTypes.map((group) =>
        group.title === RoomType.ada
          ? {
              ...group,
              options: group.options.map((option) => ({
                ...option,
                checked: applied.accessibleOnly!,
              })),
            }
          : group,
      );
      next = {
        ...next,
        roomTypes,
        roomTypesArr: getRoomTypesArr(roomTypes),
        additional: { ...next.additional, ada: applied.accessibleOnly },
      };
      mark('facilities');
    }
  }

  if (applied.maxDistanceKm === null) {
    if (next.location.km !== undefined) {
      next = { ...next, location: { ...next.location, km: undefined } };
      mark('location');
    }
  } else if (typeof applied.maxDistanceKm === 'number') {
    const km = applied.maxDistanceKm > 0 ? applied.maxDistanceKm : undefined;
    if (next.location.km !== km) {
      next = { ...next, location: { ...next.location, km } };
      mark('location');
    }
  }

  if (Array.isArray(applied.facilities)) {
    const include = resolveFacilityCodes(applied.facilities);
    if (!sameStringList(next.facilityFilter?.include || [], include)) {
      next = {
        ...next,
        facilityFilter: { include },
      };
      mark('facilities');
    }
  }

  if (Array.isArray(applied.suppliers)) {
    const needles = applied.suppliers.map((name) => name.trim().toLowerCase()).filter(Boolean);
    const suppliers = next.suppliers.map((supplier) => ({
      ...supplier,
      checked:
        needles.length > 0 &&
        (needles.includes((supplier.suppDesc || '').toLowerCase()) ||
          needles.includes((supplier.suppCode || '').toLowerCase()) ||
          needles.some(
            (needle) =>
              (supplier.suppDesc || '').toLowerCase().includes(needle) ||
              (supplier.suppCode || '').toLowerCase().includes(needle),
          )),
    }));
    const before = next.suppliers.map((supplier) => `${supplier.suppCode}:${!!supplier.checked}`).join('|');
    const after = suppliers.map((supplier) => `${supplier.suppCode}:${!!supplier.checked}`).join('|');
    if (before !== after) {
      next = { ...next, suppliers };
      mark('suppliers');
    }
  }

  const sortBy = resolveSortBy(applied.sortBy ?? undefined);
  if (sortBy && onSortChange && sortBy !== currentSortBy) {
    onSortChange(sortBy);
    if (!changedKinds.includes('sort')) {
      changedKinds.push('sort');
    }
  }

  if (changed) {
    onChangeFilter(next);
  }

  const displayMode = resolveDisplayMode(applied.displayMode);
  if (displayMode !== null && onDisplayModeChange && displayMode !== currentDisplayMode) {
    onDisplayModeChange(displayMode);
    mark('displayMode');
  }

  return { changed, applied, changedKinds };
};

// One conversation per hotel search page (Hotels 1 / Hotels 2), shared by every chat component that shows it: the tab
// in the results page's left panel and the floating widget. The page unmounts and remounts its left panel as searches
// start and finish. The conversation therefore lives here and not in a component: none of that resets it or opens a new
// session, and every copy shows the same conversation.
export interface ChatState {
  sessionId: string | null;
  // The model that answers; the backend only sends it to BePro staff.
  aiModel: string | null;
  // Whether this user gets the microphone (the backend decides: voice input on, pilot branch, vendor key) and where the
  // recorder stops by itself, in seconds.
  voiceEnabled: boolean;
  voiceMaxSeconds: number;
  messages: ChatMessage[];
  busy: boolean;
  // Replies that arrived while the floating widget was closed and the question was asked in it.
  unread: number;
}

export const EMPTY_CHAT: ChatState = {
  sessionId: null,
  aiModel: null,
  voiceEnabled: false,
  voiceMaxSeconds: 60,
  messages: [],
  busy: false,
  unread: 0,
};
const chats = new Map<number, ChatState>();
export const sessionStarts = new Set<number>();
const chatListeners = new Set<() => void>();

export const getChat = (key: number) => chats.get(key) ?? EMPTY_CHAT;

export const updateChat = (key: number, update: (chat: ChatState) => ChatState) => {
  chats.set(key, update(getChat(key)));
  chatListeners.forEach((listener) => listener());
};

export const subscribeToChats = (listener: () => void) => {
  chatListeners.add(listener);
  return () => {
    chatListeners.delete(listener);
  };
};

// Remembers the rating the user sent for one answer.
export const setMessageFeedback = (
  key: number,
  messageId: string,
  feedback: NonNullable<ChatMessage['feedback']>,
) => {
  updateChat(key, (chat) => ({
    ...chat,
    messages: chat.messages.map((m) => (m.id === messageId ? { ...m, feedback } : m)),
  }));
};

// Ends the loader of one search reply, or of every search reply of the chat.
export const finishSearchLoading = (key: number, messageId?: string) => {
  const hadLoading = getChat(key).messages.some(
    (m) => m.search?.loading && (!messageId || m.id === messageId),
  );
  if (!hadLoading) {
    return false;
  }
  updateChat(key, (chat) => ({
    ...chat,
    messages: chat.messages.map((m) =>
      m.search?.loading && (!messageId || m.id === messageId)
        ? { ...m, search: { loading: false } }
        : m,
    ),
  }));
  return true;
};

// What the page needs to run the search the assistant opened. Without a place there is nothing to open.
export const canOpenHotelSearch = (
  payload: assistantService.OpenHotelSearchPayload | undefined | null,
) => {
  const query = payload?.query;
  return !!(query?.city && query.lat && query.lon && query.id);
};

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const flightPayloadRaw = (
  payload: assistantService.OpenFlightSearchPayload | undefined | null,
): Record<string, unknown> => {
  if (!payload) {
    return {};
  }
  const root = payload as unknown as Record<string, unknown>;
  const query =
    payload.query && typeof payload.query === 'object'
      ? (payload.query as Record<string, unknown>)
      : {};
  return { ...root, ...query };
};

const toFlightBoolString = (value: unknown, fallback: boolean): string => {
  if (typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'number') {
    return value !== 0 ? 'true' : 'false';
  }
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(lower)) {
      return 'true';
    }
    if (['false', '0', 'no', 'off'].includes(lower)) {
      return 'false';
    }
  }
  return String(fallback);
};

const toFlightIntString = (value: unknown, fallback: number, min = 0): string => {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return String(Number.isFinite(parsed) && parsed >= min ? parsed : fallback);
};

const resolveFlightSearchType = (raw: Record<string, unknown>, fallback: string): string => {
  const value = firstNonEmptyString(
    raw.searchType,
    raw.tripType,
    raw.trip_type,
    raw.search_type,
    raw.flightType,
  );
  if (!value) {
    return fallback;
  }
  const lower = value.toLowerCase().replace(/\s+/g, '_');
  if (['0', 'oneway', 'one-way', 'one_way', 'ow'].includes(lower)) {
    return '0';
  }
  if (['1', 'round', 'roundtrip', 'round-trip', 'round_trip', 'rt', 'return'].includes(lower)) {
    return '1';
  }
  if (['2', 'multi', 'multicity', 'multi-city', 'multi_city', 'multiple'].includes(lower)) {
    return '2';
  }
  return fallback;
};

const resolveFlightCabinType = (raw: Record<string, unknown>, fallback: number): string => {
  const value = firstNonEmptyString(
    raw.cabineType,
    raw.cabinType,
    raw.cabin_type,
    raw.cabin,
    raw.cabinClass,
    raw.cabin_class,
    raw.class,
  );
  if (!value) {
    return String(fallback);
  }
  const lower = value.toLowerCase().replace(/\s+/g, '_');
  if (['0', 'economy', 'coach', 'y'].includes(lower)) {
    return '0';
  }
  if (
    ['3', 'premium', 'premium_economy', 'premium-economy', 'economy_premium', 'w'].includes(lower)
  ) {
    return '3';
  }
  if (['1', 'business', 'biz', 'c'].includes(lower)) {
    return '1';
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? String(parsed) : String(fallback);
};

const resolveFlightSourceDefault = (raw: Record<string, unknown>, fallback: string): string => {
  const value = firstNonEmptyString(
    raw.flightSourcesDefault,
    raw.flightSource,
    raw.flight_source,
    raw.source,
  );
  if (value === undefined) {
    return fallback;
  }
  const upper = value.trim().toUpperCase();
  if (['NONE', 'NULL', 'N/A', '-'].includes(upper)) {
    return '';
  }
  if (upper === '' || upper === 'ALP' || upper === 'ABOX') {
    return upper;
  }
  return fallback;
};

const resolveFlightDate = (value: unknown, fallback: string): string => {
  const raw = firstNonEmptyString(value);
  if (!raw) {
    return fallback;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  const date = new Date(parsed);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Destination (arrivalCode) is mandatory for a flight search; origin/dates/pax/class/filters can be defaulted.
export const canOpenFlightSearch = (
  payload: assistantService.OpenFlightSearchPayload | undefined | null,
) => {
  const raw = flightPayloadRaw(payload);
  return !!firstNonEmptyString(
    raw.arrivalCode,
    raw.destinationCode,
    raw.destination,
    raw.toCode,
    raw.to,
    raw.goingTo,
  );
};

// Turns assistant open-flight-search into URL params SearchFlightModel understands.
// Destination is required; every other search-bar field is taken from the prompt or form defaults.
export const normalizeOpenFlightSearchQuery = (
  payload: assistantService.OpenFlightSearchPayload,
): Record<string, string> => {
  const defaults = new SearchFlightModel();
  const raw = flightPayloadRaw(payload);

  const arrivalCode =
    firstNonEmptyString(
      raw.arrivalCode,
      raw.destinationCode,
      raw.destination,
      raw.toCode,
      raw.to,
      raw.goingTo,
    ) || '';
  const arrivalDesc =
    firstNonEmptyString(
      raw.arrivalDesc,
      raw.destinationDesc,
      raw.destinationLabel,
      raw.toDesc,
      raw.toLabel,
      raw.goingToLabel,
    ) || arrivalCode;

  const departureCode =
    firstNonEmptyString(
      raw.departureCode,
      raw.originCode,
      raw.origin,
      raw.fromCode,
      raw.from,
      raw.leavingFrom,
    ) || defaults.departureCode;
  const departureDesc =
    firstNonEmptyString(
      raw.departureDesc,
      raw.originDesc,
      raw.originLabel,
      raw.fromDesc,
      raw.fromLabel,
      raw.leavingFromLabel,
    ) || (departureCode === defaults.departureCode ? defaults.departureDesc : departureCode);

  const searchType = resolveFlightSearchType(raw, defaults.searchType);
  const departureDate = resolveFlightDate(
    raw.departureDate ?? raw.departDate ?? raw.outboundDate ?? raw.startDate,
    defaults.departureDate,
  );
  const arrivalDate = resolveFlightDate(
    raw.arrivalDate ?? raw.returnDate ?? raw.inboundDate ?? raw.endDate,
    defaults.arrivalDate,
  );

  const numberOfAdults = toFlightIntString(
    raw.numberOfAdults ?? raw.adults ?? raw.adult,
    defaults.numberOfAdults,
    1,
  );
  const numberOfChildren = toFlightIntString(
    raw.numberOfChildren ?? raw.children ?? raw.child,
    defaults.numberOfChildren,
  );
  const numberOfYouths = toFlightIntString(
    raw.numberOfYouths ?? raw.youths ?? raw.youth,
    defaults.numberOfYouths,
  );
  const numberOfSeniors = toFlightIntString(
    raw.numberOfSeniors ?? raw.seniors ?? raw.senior,
    defaults.numberOfSeniors,
  );
  const numberOfInfants = toFlightIntString(
    raw.numberOfInfants ?? raw.infants ?? raw.infant,
    defaults.numberOfInfants,
  );

  const bagsRaw = raw.numberOfBags ?? raw.bags ?? raw.baggage ?? raw.hasBags;
  let numberOfBags = defaults.numberOfBags;
  if (typeof bagsRaw === 'boolean') {
    numberOfBags = bagsRaw ? 1 : 0;
  } else if (bagsRaw !== undefined && bagsRaw !== null && bagsRaw !== '') {
    numberOfBags = Number.parseInt(toFlightIntString(bagsRaw, defaults.numberOfBags), 10);
  }

  const additionalFrom =
    firstNonEmptyString(raw.additionalFrom, raw.returnFrom, raw.returnOrigin) ||
    (searchType === '1' ? arrivalCode : undefined);
  const additionalTo =
    firstNonEmptyString(raw.additionalTo, raw.returnTo, raw.returnDestination) ||
    (searchType === '1' ? departureCode : undefined);
  const additionalFromLabel =
    firstNonEmptyString(raw.additionalFromLabel, raw.returnFromLabel) ||
    (additionalFrom === arrivalCode ? arrivalDesc : additionalFrom);
  const additionalToLabel =
    firstNonEmptyString(raw.additionalToLabel, raw.returnToLabel) ||
    (additionalTo === departureCode ? departureDesc : additionalTo);

  const normalized: Record<string, string> = {
    departureCode,
    departureDesc,
    arrivalCode,
    arrivalDesc,
    departureDate,
    arrivalDate,
    numberOfAdults,
    numberOfChildren,
    numberOfYouths,
    numberOfSeniors,
    numberOfInfants,
    cabineType: resolveFlightCabinType(raw, defaults.cabineType),
    searchType,
    fromValue: firstNonEmptyString(raw.fromValue) || '',
    toValue: firstNonEmptyString(raw.toValue) || '',
    directFlight: toFlightBoolString(
      raw.directFlight ?? raw.directOnly ?? raw.direct ?? raw.nonStop ?? raw.nonstop,
      defaults.directFlight,
    ),
    numberOfBags: String(numberOfBags),
    flightSourcesDefault: resolveFlightSourceDefault(raw, defaults.flightSourcesDefault || ''),
    isGds: toFlightBoolString(raw.isGds ?? raw.gds, defaults.isGds),
    isNDC: toFlightBoolString(raw.isNDC ?? raw.ndc, defaults.isNDC),
    isLowCost: toFlightBoolString(raw.isLowCost ?? raw.lowCost ?? raw.lcc, defaults.isLowCost),
  };

  if (additionalFrom) {
    normalized.additionalFrom = additionalFrom;
  }
  if (additionalTo) {
    normalized.additionalTo = additionalTo;
  }
  if (additionalFromLabel) {
    normalized.additionalFromLabel = additionalFromLabel;
  }
  if (additionalToLabel) {
    normalized.additionalToLabel = additionalToLabel;
  }

  return normalized;
};

// Sparse DTO from the backend for flight result filters (sidebar: stops, baggage, price, times, …).
export interface AppliedFlightFilters {
  stops?: number[] | null;
  baggageIncluded?: boolean | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  departureOutboundTimeMin?: number | string | null;
  departureOutboundTimeMax?: number | string | null;
  arrivalOutboundTimeMin?: number | string | null;
  arrivalOutboundTimeMax?: number | string | null;
  departureInboundTimeMin?: number | string | null;
  departureInboundTimeMax?: number | string | null;
  arrivalInboundTimeMin?: number | string | null;
  arrivalInboundTimeMax?: number | string | null;
  durationMin?: number | null;
  durationMax?: number | null;
  stopDurationMin?: number | null;
  stopDurationMax?: number | null;
  airlines?: string[] | null;
  airports?: string[] | null;
  flightTypes?: string[] | null;
  freeText?: string | null;
  sortBy?: string | null;
}

const APPLIED_FLIGHT_FILTER_KEY_ALIASES: Record<string, keyof AppliedFlightFilters> = {
  stops: 'stops',
  stop: 'stops',
  direct: 'stops',
  directonly: 'stops',
  direct_only: 'stops',
  baggageincluded: 'baggageIncluded',
  baggage_included: 'baggageIncluded',
  baggage: 'baggageIncluded',
  bags: 'baggageIncluded',
  minprice: 'minPrice',
  min_price: 'minPrice',
  maxprice: 'maxPrice',
  max_price: 'maxPrice',
  departureoutboundtimemin: 'departureOutboundTimeMin',
  departure_outbound_time_min: 'departureOutboundTimeMin',
  takeoffoutboundmin: 'departureOutboundTimeMin',
  takeoff_outbound_min: 'departureOutboundTimeMin',
  departureoutboundtimemax: 'departureOutboundTimeMax',
  departure_outbound_time_max: 'departureOutboundTimeMax',
  takeoffoutboundmax: 'departureOutboundTimeMax',
  takeoff_outbound_max: 'departureOutboundTimeMax',
  arrivaloutboundtimemin: 'arrivalOutboundTimeMin',
  arrival_outbound_time_min: 'arrivalOutboundTimeMin',
  landingoutboundmin: 'arrivalOutboundTimeMin',
  landing_outbound_min: 'arrivalOutboundTimeMin',
  arrivaloutboundtimemax: 'arrivalOutboundTimeMax',
  arrival_outbound_time_max: 'arrivalOutboundTimeMax',
  landingoutboundmax: 'arrivalOutboundTimeMax',
  landing_outbound_max: 'arrivalOutboundTimeMax',
  departureinboundtimemin: 'departureInboundTimeMin',
  departure_inbound_time_min: 'departureInboundTimeMin',
  takeoffinboundmin: 'departureInboundTimeMin',
  takeoff_inbound_min: 'departureInboundTimeMin',
  departureinboundtimemax: 'departureInboundTimeMax',
  departure_inbound_time_max: 'departureInboundTimeMax',
  takeoffinboundmax: 'departureInboundTimeMax',
  takeoff_inbound_max: 'departureInboundTimeMax',
  arrivalinboundtimemin: 'arrivalInboundTimeMin',
  arrival_inbound_time_min: 'arrivalInboundTimeMin',
  landinginboundmin: 'arrivalInboundTimeMin',
  landing_inbound_min: 'arrivalInboundTimeMin',
  arrivalinboundtimemax: 'arrivalInboundTimeMax',
  arrival_inbound_time_max: 'arrivalInboundTimeMax',
  landinginboundmax: 'arrivalInboundTimeMax',
  landing_inbound_max: 'arrivalInboundTimeMax',
  durationmin: 'durationMin',
  duration_min: 'durationMin',
  minduration: 'durationMin',
  min_duration: 'durationMin',
  durationmax: 'durationMax',
  duration_max: 'durationMax',
  maxduration: 'durationMax',
  max_duration: 'durationMax',
  stopdurationmin: 'stopDurationMin',
  stop_duration_min: 'stopDurationMin',
  minstopduration: 'stopDurationMin',
  min_stop_duration: 'stopDurationMin',
  minlayover: 'stopDurationMin',
  min_layover: 'stopDurationMin',
  stopdurationmax: 'stopDurationMax',
  stop_duration_max: 'stopDurationMax',
  maxstopduration: 'stopDurationMax',
  max_stop_duration: 'stopDurationMax',
  maxlayover: 'stopDurationMax',
  max_layover: 'stopDurationMax',
  layovermax: 'stopDurationMax',
  layover_max: 'stopDurationMax',
  airlines: 'airlines',
  airline: 'airlines',
  carriers: 'airlines',
  carrier: 'airlines',
  airports: 'airports',
  airport: 'airports',
  flighttypes: 'flightTypes',
  flight_types: 'flightTypes',
  flightsources: 'flightTypes',
  flight_sources: 'flightTypes',
  sources: 'flightTypes',
  flights: 'flightTypes',
  freetext: 'freeText',
  free_text: 'freeText',
  text: 'freeText',
  sortby: 'sortBy',
  sort_by: 'sortBy',
  sort: 'sortBy',
};

const TIME_PERIOD_HOURS: Record<string, [number, number]> = {
  morning: [5, 12],
  afternoon: [12, 17],
  evening: [17, 21],
  night: [21, 24],
  early: [0, 8],
  late: [18, 24],
};

const normalizeDurationMinutes = (value: unknown): number | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 0 && value <= 48 ? Math.round(value * 60) : Math.round(value);
  }
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const raw = value.trim().toLowerCase();
  const hoursMatch = raw.match(/(\d+(?:\.\d+)?)\s*h/);
  const minsMatch = raw.match(/(\d+(?:\.\d+)?)\s*m/);
  if (hoursMatch || minsMatch) {
    const hours = hoursMatch ? Number.parseFloat(hoursMatch[1]) : 0;
    const mins = minsMatch ? Number.parseFloat(minsMatch[1]) : 0;
    return Math.round(hours * 60 + mins);
  }
  const asNumber = Number.parseFloat(raw);
  if (!Number.isFinite(asNumber)) {
    return undefined;
  }
  return asNumber >= 0 && asNumber <= 48 ? Math.round(asNumber * 60) : Math.round(asNumber);
};

const readRangeObject = (
  value: unknown,
): { min?: number | string | null; max?: number | string | null } | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const item = value as Record<string, unknown>;
  const min = item.min ?? item.from ?? item.start ?? item.minValue ?? item.min_value;
  const max = item.max ?? item.to ?? item.end ?? item.maxValue ?? item.max_value;
  return {
    min: min === undefined ? undefined : (min as number | string | null),
    max: max === undefined ? undefined : (max as number | string | null),
  };
};

const resolvePeriodOrTime = (
  value: unknown,
  edge: 'min' | 'max',
): number | string | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number' || value === undefined) {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const lower = value.trim().toLowerCase().replace(/\s+/g, '_');
  const period = TIME_PERIOD_HOURS[lower];
  if (period) {
    return edge === 'min' ? period[0] : period[1];
  }
  return value;
};

const mergeTimeRange = (
  filters: AppliedFlightFilters,
  minKey: keyof AppliedFlightFilters,
  maxKey: keyof AppliedFlightFilters,
  range: { min?: number | string | null; max?: number | string | null } | null | undefined,
) => {
  if (!range) {
    return false;
  }
  let changed = false;
  if (range.min !== undefined) {
    (filters as Record<string, unknown>)[minKey] = resolvePeriodOrTime(range.min, 'min');
    changed = true;
  }
  if (range.max !== undefined) {
    (filters as Record<string, unknown>)[maxKey] = resolvePeriodOrTime(range.max, 'max');
    changed = true;
  }
  return changed;
};

const applyNestedFlightTimeFilters = (
  source: Record<string, unknown>,
  filters: AppliedFlightFilters,
): boolean => {
  let changed = false;
  const timesRoot =
    (source.times as Record<string, unknown> | undefined) ||
    (source.time as Record<string, unknown> | undefined) ||
    source;

  const takeoff =
    (timesRoot.takeoff as Record<string, unknown> | undefined) ||
    (timesRoot.take_off as Record<string, unknown> | undefined) ||
    (timesRoot.takeOff as Record<string, unknown> | undefined) ||
    (timesRoot.departure as Record<string, unknown> | undefined);
  const landing =
    (timesRoot.landing as Record<string, unknown> | undefined) ||
    (timesRoot.arrival as Record<string, unknown> | undefined);

  const readLeg = (root: Record<string, unknown> | undefined, leg: 'outbound' | 'inbound') => {
    if (!root) {
      return null;
    }
    const nested =
      (root[leg] as unknown) ||
      (leg === 'outbound'
        ? root.out || root.going || root.departure
        : root.in || root.return || root.inbound || root.returning);
    return readRangeObject(nested) || (leg === 'outbound' ? readRangeObject(root) : null);
  };

  if (takeoff && typeof takeoff === 'object') {
    changed =
      mergeTimeRange(
        filters,
        'departureOutboundTimeMin',
        'departureOutboundTimeMax',
        readLeg(takeoff, 'outbound'),
      ) || changed;
    changed =
      mergeTimeRange(
        filters,
        'departureInboundTimeMin',
        'departureInboundTimeMax',
        readLeg(takeoff, 'inbound'),
      ) || changed;
  }

  if (landing && typeof landing === 'object') {
    changed =
      mergeTimeRange(
        filters,
        'arrivalOutboundTimeMin',
        'arrivalOutboundTimeMax',
        readLeg(landing, 'outbound'),
      ) || changed;
    changed =
      mergeTimeRange(
        filters,
        'arrivalInboundTimeMin',
        'arrivalInboundTimeMax',
        readLeg(landing, 'inbound'),
      ) || changed;
  }

  const durationObj = readRangeObject(source.duration);
  if (durationObj) {
    if (durationObj.min !== undefined) {
      filters.durationMin = normalizeDurationMinutes(durationObj.min);
      changed = true;
    }
    if (durationObj.max !== undefined) {
      filters.durationMax = normalizeDurationMinutes(durationObj.max);
      changed = true;
    }
  }

  const stopDurationObj =
    readRangeObject(source.stopDuration) ||
    readRangeObject(source.stop_duration) ||
    readRangeObject(source.layover) ||
    readRangeObject(source.connection);
  if (stopDurationObj) {
    if (stopDurationObj.min !== undefined) {
      filters.stopDurationMin = normalizeDurationMinutes(stopDurationObj.min);
      changed = true;
    }
    if (stopDurationObj.max !== undefined) {
      filters.stopDurationMax = normalizeDurationMinutes(stopDurationObj.max);
      changed = true;
    }
  }

  return changed;
};

const normalizeStopNumbers = (value: unknown): number[] | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value ? [0] : undefined;
  }
  const items = Array.isArray(value)
    ? value
    : typeof value === 'string' || typeof value === 'number'
      ? [value]
      : null;
  if (!items) {
    return undefined;
  }
  const stops = items
    .flatMap((item) => {
      if (typeof item === 'number' && Number.isFinite(item)) {
        return [item];
      }
      const raw = String(item).trim().toLowerCase().replace(/\s+/g, '_');
      if (!raw) {
        return [];
      }
      if (['0', 'direct', 'nonstop', 'non_stop', 'direct_only', 'directflights'].includes(raw)) {
        return [0];
      }
      if (['0.5', 'direct_outbound', 'direct_outbound_only', 'outbound_direct'].includes(raw)) {
        return [0.5];
      }
      if (['1', '1_stop', 'one_stop', 'onestop'].includes(raw)) {
        return [1];
      }
      if (['2', '2+', '2_plus', 'two_plus', 'two_or_more', '2_or_more'].includes(raw)) {
        return [2];
      }
      const parsed = Number.parseFloat(raw);
      return Number.isFinite(parsed) ? [parsed] : [];
    })
    .filter((stop, index, arr) => arr.indexOf(stop) === index);
  return stops.length ? stops : undefined;
};

const normalizeBaggageIncluded = (value: unknown): boolean | null | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    if (value === -1) {
      return null;
    }
    return undefined;
  }
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase().replace(/\s+/g, '_');
    if (['all', 'any', 'clear', ''].includes(lower)) {
      return null;
    }
    if (['true', '1', 'yes', 'included', 'with_baggage', 'with_bags', 'baggage'].includes(lower)) {
      return true;
    }
    if (
      ['false', '0', 'no', 'not_included', 'without_baggage', 'without_bags', 'no_baggage'].includes(
        lower,
      )
    ) {
      return false;
    }
  }
  return normalizeBoolean(value);
};

const normalizeNeedleList = (value: unknown): string[] | null | undefined => {
  const list = normalizeStringArray(value);
  if (!list) {
    return list;
  }
  return list.map((item) => item.trim()).filter(Boolean);
};

const optionMatchesNeedle = (
  option: { code: string; label?: string },
  needles: string[],
): boolean => {
  if (!needles.length) {
    return false;
  }
  const code = (option.code || '').trim().toLowerCase();
  const label = (option.label || '').trim().toLowerCase();
  return needles.some((needle) => {
    const wanted = needle.trim().toLowerCase();
    if (!wanted) {
      return false;
    }
    if (code === wanted || label === wanted) {
      return true;
    }
    if (wanted.length >= 2 && (code.includes(wanted) || label.includes(wanted))) {
      return true;
    }
    if (code.length >= 2 && wanted.includes(code)) {
      return true;
    }
    if (label.length >= 3 && wanted.includes(label)) {
      return true;
    }
    return false;
  });
};

const sameOptionChecks = (
  options: { code: string; label?: string; checked: boolean }[],
  needles: string[] | null | undefined,
) => {
  if (!Array.isArray(needles)) {
    return true;
  }
  return options.every((option) => option.checked === optionMatchesNeedle(option, needles));
};

export const parseAppliedFlightFilters = (
  raw: Record<string, unknown> | null | undefined,
): AppliedFlightFilters | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const source = pickFilterSource(raw);
  const filters: AppliedFlightFilters = {};
  let hasFilterKey = false;

  Object.entries(source).forEach(([key, value]) => {
    const normalizedKey = key.replace(/-/g, '_').toLowerCase();
    const mappedKey = APPLIED_FLIGHT_FILTER_KEY_ALIASES[normalizedKey];
    if (!mappedKey) {
      return;
    }
    hasFilterKey = true;

    switch (mappedKey) {
      case 'stops':
        filters.stops = normalizeStopNumbers(value);
        break;
      case 'baggageIncluded':
        filters.baggageIncluded = normalizeBaggageIncluded(value);
        break;
      case 'minPrice':
      case 'maxPrice':
        filters[mappedKey] = normalizeNumber(value);
        break;
      case 'durationMin':
      case 'durationMax':
      case 'stopDurationMin':
      case 'stopDurationMax':
        filters[mappedKey] = normalizeDurationMinutes(value);
        break;
      case 'departureOutboundTimeMin':
      case 'departureOutboundTimeMax':
      case 'arrivalOutboundTimeMin':
      case 'arrivalOutboundTimeMax':
      case 'departureInboundTimeMin':
      case 'departureInboundTimeMax':
      case 'arrivalInboundTimeMin':
      case 'arrivalInboundTimeMax': {
        const edge = mappedKey.endsWith('Min') ? 'min' : 'max';
        const resolved = resolvePeriodOrTime(value, edge);
        if (resolved === null || typeof resolved === 'number' || typeof resolved === 'string') {
          filters[mappedKey] = resolved;
        }
        break;
      }
      case 'airlines':
      case 'airports':
      case 'flightTypes':
        filters[mappedKey] = normalizeNeedleList(value);
        break;
      case 'freeText':
      case 'sortBy':
        filters[mappedKey] =
          value === null ? null : typeof value === 'string' ? value : String(value ?? '');
        break;
      default:
        break;
    }
  });

  if (applyNestedFlightTimeFilters(source, filters)) {
    hasFilterKey = true;
  }

  return hasFilterKey ? filters : null;
};

const resolveFlightFilterTimeValue = (value: number | string, bounds: TimeFilter): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 0 && value <= 24) {
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 60);
      const base = new Date(bounds.min);
      base.setHours(hours === 24 ? 23 : hours, hours === 24 ? 59 : minutes, 0, 0);
      return base.getTime();
    }
    return value;
  }
  const raw = String(value).trim();
  const period = TIME_PERIOD_HOURS[raw.toLowerCase().replace(/\s+/g, '_')];
  if (period) {
    const base = new Date(bounds.min);
    base.setHours(period[0] === 24 ? 23 : period[0], period[0] === 24 ? 59 : 0, 0, 0);
    return base.getTime();
  }
  if (/^\d{1,2}:\d{2}$/.test(raw)) {
    const [hours, minutes] = raw.split(':').map((part) => Number.parseInt(part, 10));
    const base = new Date(bounds.min);
    base.setHours(hours, minutes, 0, 0);
    return base.getTime();
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : bounds.min;
};

const clampFlightRange = (
  bounds: TimeFilter | FlightFilterModel['price'],
  minVal: number | string | null | undefined,
  maxVal: number | string | null | undefined,
  asTime = false,
): [number, number] | null => {
  if (minVal === undefined && maxVal === undefined) {
    return null;
  }
  let [low, high] = bounds.value;
  const resolve = (value: number | string) =>
    asTime && 'min' in bounds
      ? resolveFlightFilterTimeValue(value, bounds as TimeFilter)
      : typeof value === 'number'
        ? value
        : Number(value);

  if (minVal === null) {
    low = bounds.min;
  } else if (minVal !== undefined) {
    const resolved = resolve(minVal);
    if (Number.isFinite(resolved)) {
      low = Math.max(bounds.min, Math.min(resolved, bounds.max || resolved));
    }
  }
  if (maxVal === null) {
    high = bounds.max;
  } else if (maxVal !== undefined) {
    const resolved = resolve(maxVal);
    if (Number.isFinite(resolved)) {
      high = Math.max(low, Math.min(resolved, bounds.max || resolved));
    }
  }
  if (low > high) {
    low = high;
  }
  if (low === bounds.value[0] && high === bounds.value[1]) {
    return null;
  }
  return [low, high];
};

export const applyAssistantFlightFilters = (
  filter: FlightFilterModel,
  applied: AppliedFlightFilters | null | undefined,
  actions?: {
    onChangeFilter: (filter: FlightFilterModel) => void;
    onSortChange?: (sort: string) => void;
    currentSortBy?: string;
  },
): {
  changed: boolean;
  applied: AppliedFlightFilters | null;
  changedKinds: AssistantFilterHighlightKind[];
  next: FlightFilterModel;
} => {
  if (!applied) {
    return { changed: false, applied: null, changedKinds: [], next: filter };
  }

  let next = filter;
  let changed = false;
  const changedKinds: AssistantFilterHighlightKind[] = [];
  const mark = (kind: AssistantFilterHighlightKind) => {
    if (!changedKinds.includes(kind)) {
      changedKinds.push(kind);
    }
    changed = true;
  };

  if (Array.isArray(applied.stops) || applied.stops === null) {
    const wanted = new Set(Array.isArray(applied.stops) ? applied.stops : []);
    const stops = next.stops.map((stop) => ({
      ...stop,
      checked: wanted.has(stop.number),
    }));
    if (stops.some((stop, index) => stop.checked !== next.stops[index].checked)) {
      next = { ...next, stops };
      mark('stops');
    }
  }

  if (applied.baggageIncluded !== undefined) {
    const baggage = next.baggage.map((bag) => ({
      ...bag,
      checked:
        applied.baggageIncluded === null
          ? false
          : applied.baggageIncluded
            ? bag.number === 1
            : bag.number === 0,
    }));
    if (baggage.some((bag, index) => bag.checked !== next.baggage[index].checked)) {
      next = { ...next, baggage };
      mark('baggage');
    }
  }

  const priceRange = clampFlightRange(next.price, applied.minPrice, applied.maxPrice);
  if (priceRange) {
    next = { ...next, price: { ...next.price, value: priceRange } };
    mark('price');
  }

  const applyTime = (
    key:
      | 'departureOutboundTime'
      | 'arrivalOutboundTime'
      | 'departureInboundTime'
      | 'arrivalInboundTime',
    minVal: number | string | null | undefined,
    maxVal: number | string | null | undefined,
  ) => {
    const current = next[key];
    if (!current) {
      return;
    }
    const range = clampFlightRange(current, minVal, maxVal, true);
    if (!range) {
      return;
    }
    next = { ...next, [key]: { ...current, value: range } };
    mark('times');
  };

  applyTime(
    'departureOutboundTime',
    applied.departureOutboundTimeMin,
    applied.departureOutboundTimeMax,
  );
  applyTime('arrivalOutboundTime', applied.arrivalOutboundTimeMin, applied.arrivalOutboundTimeMax);
  applyTime(
    'departureInboundTime',
    applied.departureInboundTimeMin,
    applied.departureInboundTimeMax,
  );
  applyTime('arrivalInboundTime', applied.arrivalInboundTimeMin, applied.arrivalInboundTimeMax);

  const durationRange = clampFlightRange(next.duration, applied.durationMin, applied.durationMax);
  if (durationRange) {
    next = { ...next, duration: { ...next.duration, value: durationRange } };
    mark('duration');
  }

  const stopDurationRange = clampFlightRange(
    next.stopDuration,
    applied.stopDurationMin,
    applied.stopDurationMax,
  );
  if (stopDurationRange) {
    next = { ...next, stopDuration: { ...next.stopDuration, value: stopDurationRange } };
    mark('duration');
  }

  if (Array.isArray(applied.airlines) || applied.airlines === null) {
    const needles = applied.airlines || [];
    if (!sameOptionChecks(next.airlines, needles)) {
      next = {
        ...next,
        airlines: next.airlines.map((airline) => ({
          ...airline,
          checked: optionMatchesNeedle(airline, needles),
        })),
      };
      mark('airlines');
    }
  }

  if (Array.isArray(applied.airports) || applied.airports === null) {
    const needles = applied.airports || [];
    if (!sameOptionChecks(next.airports, needles)) {
      next = {
        ...next,
        airports: next.airports.map((airport) => ({
          ...airport,
          checked: optionMatchesNeedle(airport, needles),
        })),
      };
      mark('airports');
    }
  }

  if (Array.isArray(applied.flightTypes) || applied.flightTypes === null) {
    const needles = applied.flightTypes || [];
    if (!sameOptionChecks(next.flightTypes, needles)) {
      next = {
        ...next,
        flightTypes: next.flightTypes.map((type) => ({
          ...type,
          checked: optionMatchesNeedle(type, needles),
        })),
      };
      mark('flightTypes');
    }
  }

  if (typeof applied.freeText === 'string' && next.freeText !== applied.freeText) {
    next = { ...next, freeText: applied.freeText };
    mark('name');
  } else if (applied.freeText === null && next.freeText) {
    next = { ...next, freeText: '' };
    mark('name');
  }

  if (changed && actions?.onChangeFilter) {
    actions.onChangeFilter(next);
  }

  if (
    typeof applied.sortBy === 'string' &&
    applied.sortBy &&
    actions?.onSortChange &&
    applied.sortBy !== actions.currentSortBy
  ) {
    actions.onSortChange(applied.sortBy);
    mark('sort');
  }

  return { changed, applied, changedKinds, next };
};

type AssistantSearchRoom = {
  adults: number;
  childrenAges: number[];
};

const toPositiveInt = (value: unknown, fallback: number) => {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const toAge = (value: unknown, fallback = 2) => {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseRoomsStringValue = (value: string): AssistantSearchRoom[] =>
  value
    .split(';')
    .map((room) => room.trim())
    .filter(Boolean)
    .map((room) => {
      const [adultsRaw, ...childrenRaw] = room.split('_');
      return {
        adults: toPositiveInt(adultsRaw, 2),
        childrenAges: childrenRaw.map((age) => toAge(age)).filter((age) => age >= 0),
      };
    })
    .filter((room) => room.adults > 0);

const roomsFromUnknown = (value: unknown): AssistantSearchRoom[] => {
  if (!value) {
    return [];
  }
  if (typeof value === 'string') {
    return parseRoomsStringValue(value);
  }
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((room) => {
      if (!room || typeof room !== 'object') {
        return null;
      }
      const item = room as Record<string, unknown>;
      const adults = toPositiveInt(item.adults ?? item.adult ?? item.numAdt ?? item.adt, 2);
      const agesRaw =
        item.childrenAges ??
        item.children_ages ??
        item.childAges ??
        item.child_ages ??
        item.ages ??
        item.children;
      let childrenAges: number[] = [];
      if (Array.isArray(agesRaw)) {
        childrenAges = agesRaw.map((age) => {
          if (age && typeof age === 'object' && 'age' in (age as object)) {
            return toAge((age as { age: unknown }).age);
          }
          return toAge(age);
        });
      } else if (typeof agesRaw === 'number' || typeof agesRaw === 'string') {
        const childrenCount = toPositiveInt(agesRaw, 0);
        childrenAges = Array.from({ length: childrenCount }, () => 2);
      }
      return { adults, childrenAges };
    })
    .filter((room): room is AssistantSearchRoom => !!room && room.adults > 0);
};

const buildRoomsString = (rooms: AssistantSearchRoom[]) =>
  rooms
    .map((room) => {
      const ages = room.childrenAges.map((age) => String(age)).join('_');
      return ages ? `${room.adults}_${ages}` : String(room.adults);
    })
    .join(';');

const resolveRoomsString = (
  query: Record<string, unknown>,
  payload: Record<string, unknown>,
): string => {
  const fromQueryString =
    typeof query.roomsString === 'string' && query.roomsString.trim()
      ? parseRoomsStringValue(query.roomsString)
      : [];
  if (fromQueryString.length) {
    return buildRoomsString(fromQueryString);
  }

  const structured = [
    roomsFromUnknown(query.rooms),
    roomsFromUnknown(payload.rooms),
    roomsFromUnknown(query.occupancy),
    roomsFromUnknown(payload.occupancy),
  ].find((rooms) => rooms.length);

  if (structured?.length) {
    return buildRoomsString(structured);
  }

  const adults = toPositiveInt(
    query.adults ?? query.adult ?? payload.adults ?? payload.adult,
    0,
  );
  const childrenCount = toPositiveInt(
    query.children ?? query.child ?? payload.children ?? payload.child,
    0,
  );
  const agesRaw =
    query.childrenAges ??
    query.children_ages ??
    query.childAges ??
    query.ages ??
    payload.childrenAges ??
    payload.children_ages ??
    payload.childAges ??
    payload.ages;

  if (adults > 0) {
    let childrenAges: number[] = [];
    if (Array.isArray(agesRaw)) {
      childrenAges = agesRaw.map((age) => toAge(age));
    } else if (childrenCount > 0) {
      childrenAges = Array.from({ length: childrenCount }, () => 2);
    }
    return buildRoomsString([{ adults, childrenAges }]);
  }

  const current =
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('roomsString')) ||
    (typeof window !== 'undefined' && localStorage.getItem('roomsStringHotel')) ||
    '';
  if (current && parseRoomsStringValue(current).length) {
    return current;
  }

  return '2';
};

// Search-bar Stars = supplier minimum (starRating). Result Stars = filter chips 3/4/5.
// Assistant searches always use starRating=1; star preferences go to the result filter.
export const extractStarsFilterFromSearchPayload = (
  payload: assistantService.OpenHotelSearchPayload,
): number[] | null => {
  const fromApplied = parseAppliedHotelFilters(
    payload.appliedFilters as Record<string, unknown> | null | undefined,
  )?.stars;
  if (Array.isArray(fromApplied)) {
    return fromApplied;
  }

  const rawQuery = (payload.query || {}) as Record<string, unknown>;
  const rawPayload = payload as unknown as Record<string, unknown>;
  const fromQueryStars = normalizeStars(rawQuery.stars ?? rawPayload.stars);
  if (Array.isArray(fromQueryStars) && fromQueryStars.length) {
    return fromQueryStars;
  }

  const rating = normalizeNumber(rawQuery.starRating ?? rawQuery.star_rating ?? rawPayload.starRating);
  if (typeof rating === 'number' && rating >= 3 && rating <= 5) {
    return Array.from({ length: 6 - rating }, (_, index) => rating + index);
  }

  return null;
};

// Turns assistant open-hotel-search query into URL params SearchHotelModel understands, including roomsString.
export const normalizeOpenHotelSearchQuery = (
  payload: assistantService.OpenHotelSearchPayload,
): Record<string, string> => {
  const rawQuery = (payload.query || {}) as Record<string, unknown>;
  const rawPayload = payload as unknown as Record<string, unknown>;
  const roomsString = resolveRoomsString(rawQuery, rawPayload);

  const normalized: Record<string, string> = {};
  Object.entries(rawQuery).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    if (key === 'stars' || key === 'starRating' || key === 'star_rating') {
      return;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      normalized[key] = String(value);
    }
  });

  normalized.roomsString = roomsString;
  normalized.starRating = '1';

  if (roomsString !== '2') {
    try {
      localStorage.setItem('roomsStringHotel', roomsString);
    } catch {
      // Search still works from the URL param alone.
    }
  }

  return normalized;
};

const APPLY_HOTEL_FILTERS_ALIASES = [
  assistantService.APPLY_HOTEL_FILTERS,
  'set-filters',
  'set_filters',
  'setFilters',
] as const;

export const isApplyHotelFiltersCommand = (type?: string | null) =>
  !!type &&
  APPLY_HOTEL_FILTERS_ALIASES.some(
    (alias) => alias.toLowerCase() === type.trim().toLowerCase(),
  );

const readKeepFiltersFlag = (payload: assistantService.OpenHotelSearchPayload) => {
  const raw = payload as unknown as Record<string, unknown>;
  const candidates = [
    raw.keepFilters,
    raw.keep_filters,
    raw.searchWithActiveFilters,
    raw.keepFilter,
    raw.keep_filter,
    raw.withActiveFilters,
    raw.with_active_filters,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) {
      continue;
    }
    if (typeof candidate === 'boolean') {
      return candidate;
    }
    if (typeof candidate === 'string') {
      const normalized = candidate.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
        return true;
      }
      if (normalized === 'false' || normalized === '0' || normalized === 'no') {
        return false;
      }
    }
    if (candidate === 1) {
      return true;
    }
    if (candidate === 0) {
      return false;
    }
  }

  return null;
};

// Same as pressing the results "search with active filters" lock before a new search.
// Only keeps filters when the command explicitly asks; "not keep filters" / absent flag turns the lock off.
export const shouldKeepFiltersOnSearch = (
  payload: assistantService.OpenHotelSearchPayload,
) => {
  const explicit = readKeepFiltersFlag(payload);
  if (explicit !== null) {
    return explicit;
  }
  return false;
};

export const enableSearchWithActiveFilters = (currentFilter?: FilterModel | null) => {
  try {
    if (currentFilter) {
      sessionStorage.setItem(
        'filterHotels',
        JSON.stringify({ ...currentFilter, searchWithActiveFilters: true }),
      );
      return;
    }
    const raw = sessionStorage.getItem('filterHotels');
    if (!raw) {
      return;
    }
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== 'object') {
      return;
    }
    stored.searchWithActiveFilters = true;
    sessionStorage.setItem('filterHotels', JSON.stringify(stored));
  } catch {
    // Search still runs; filters may reset if the lock could not be set.
  }
};

export const disableSearchWithActiveFilters = (currentFilter?: FilterModel | null) => {
  try {
    if (currentFilter) {
      sessionStorage.setItem(
        'filterHotels',
        JSON.stringify({ ...currentFilter, searchWithActiveFilters: false }),
      );
      return;
    }
    const raw = sessionStorage.getItem('filterHotels');
    if (!raw) {
      return;
    }
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== 'object') {
      return;
    }
    stored.searchWithActiveFilters = false;
    sessionStorage.setItem('filterHotels', JSON.stringify(stored));
  } catch {
    // Ignore.
  }
};

// The chat of the results page "Hotels 2" is its own; every other page shares the first one.
export const chatKeyForPath = (pathname: string) => (/^\/hotels?2(\/|$)/.test(pathname) ? 2 : 0);

// ---------------------------------------------------------------------------------------------------------------
// Filters on their way to the results page. The chat may be anywhere in the app (the floating widget is), but the
// filter lives in the results page, so the chat only queues them here and AssistantPageBridge, inside that page,
// applies them.
// ---------------------------------------------------------------------------------------------------------------

export interface PendingFilters {
  filters: AppliedHotelFilters;
  // True while the page has no hotels to filter yet: filters sent along with a new search, or sent while the page
  // had no filter (it clears its filter when a search starts and rebuilds it as hotels arrive). They wait for the
  // rebuilt filter to have hotels (its price range is known), then apply once. The rebuilt filter keeps these values
  // as later hotels merge in.
  waitForHotels: boolean;
  expiresAt: number;
}

const pendingFilters = new Map<number, PendingFilters>();
const pendingListeners = new Set<() => void>();
let pendingVersion = 0;

const notifyPending = () => {
  pendingVersion += 1;
  pendingListeners.forEach((listener) => listener());
};

export const subscribeToPendingFilters = (listener: () => void) => {
  pendingListeners.add(listener);
  return () => {
    pendingListeners.delete(listener);
  };
};

// Changes whenever filters are queued, so a component can react to the queue.
export const getPendingFiltersVersion = () => pendingVersion;

export const queueFilters = (key: number, filters: AppliedHotelFilters, waitForHotels = false) => {
  pendingFilters.set(key, {
    filters,
    waitForHotels,
    expiresAt: Date.now() + PENDING_FILTERS_TTL_MS,
  });
  notifyPending();
};

export const dropPendingFilters = (key: number) => {
  if (pendingFilters.delete(key)) {
    notifyPending();
  }
};

// The filters waiting for this page, or null (none, or they expired).
export const peekPendingFilters = (key: number): PendingFilters | null => {
  const pending = pendingFilters.get(key);
  if (!pending) {
    return null;
  }
  if (Date.now() > pending.expiresAt) {
    pendingFilters.delete(key);
    return null;
  }
  return pending;
};

// Taking is silent: the page that takes them is the one that applies them.
export const takePendingFilters = (key: number) => {
  const pending = peekPendingFilters(key);
  pendingFilters.delete(key);
  return pending?.filters ?? null;
};

// ---------------------------------------------------------------------------------------------------------------
// Flight result filters on their way to /flights/results (apply-flight-filters / open-flight-search).
// ---------------------------------------------------------------------------------------------------------------

export interface PendingFlightFilters {
  filters: AppliedFlightFilters;
  waitForFlights: boolean;
  expiresAt: number;
}

const pendingFlightFilters = new Map<number, PendingFlightFilters>();
const pendingFlightListeners = new Set<() => void>();
let pendingFlightVersion = 0;

const notifyPendingFlight = () => {
  pendingFlightVersion += 1;
  pendingFlightListeners.forEach((listener) => listener());
};

export const subscribeToPendingFlightFilters = (listener: () => void) => {
  pendingFlightListeners.add(listener);
  return () => {
    pendingFlightListeners.delete(listener);
  };
};

export const getPendingFlightFiltersVersion = () => pendingFlightVersion;

export const queueFlightFilters = (
  key: number,
  filters: AppliedFlightFilters,
  waitForFlights = false,
) => {
  pendingFlightFilters.set(key, {
    filters,
    waitForFlights,
    expiresAt: Date.now() + PENDING_FILTERS_TTL_MS,
  });
  notifyPendingFlight();
};

export const dropPendingFlightFilters = (key: number) => {
  if (pendingFlightFilters.delete(key)) {
    notifyPendingFlight();
  }
};

export const peekPendingFlightFilters = (key: number): PendingFlightFilters | null => {
  const pending = pendingFlightFilters.get(key);
  if (!pending) {
    return null;
  }
  if (Date.now() > pending.expiresAt) {
    pendingFlightFilters.delete(key);
    return null;
  }
  return pending;
};

export const takePendingFlightFilters = (key: number) => {
  const pending = peekPendingFlightFilters(key);
  pendingFlightFilters.delete(key);
  return pending?.filters ?? null;
};

// ---------------------------------------------------------------------------------------------------------------
// Hotel-card clicks on their way to the results page (hotel-card-action UI command).
// ---------------------------------------------------------------------------------------------------------------

export type PendingHotelCardAction = assistantService.HotelCardActionPayload;

const pendingCardActions = new Map<number, PendingHotelCardAction[]>();
const cardActionListeners = new Set<() => void>();
let cardActionVersion = 0;

const notifyCardActions = () => {
  cardActionVersion += 1;
  cardActionListeners.forEach((listener) => listener());
};

export const subscribeToPendingCardActions = (listener: () => void) => {
  cardActionListeners.add(listener);
  return () => {
    cardActionListeners.delete(listener);
  };
};

export const getPendingCardActionsVersion = () => cardActionVersion;

export const queueHotelCardAction = (key: number, action: PendingHotelCardAction) => {
  const queue = pendingCardActions.get(key) ?? [];
  queue.push(action);
  pendingCardActions.set(key, queue);
  notifyCardActions();
};

export const peekPendingHotelCardAction = (key: number) => {
  const queue = pendingCardActions.get(key);
  return queue?.[0] ?? null;
};

export const takePendingHotelCardAction = (key: number) => {
  const queue = pendingCardActions.get(key);
  if (!queue?.length) {
    return null;
  }
  const action = queue.shift()!;
  if (queue.length) {
    pendingCardActions.set(key, queue);
  } else {
    pendingCardActions.delete(key);
  }
  notifyCardActions();
  return action;
};

export const hasPendingHotelCardAction = (key: number) =>
  (pendingCardActions.get(key)?.length ?? 0) > 0;

export type PageHotelRef = { name: string; htuKey: string };
export type ActiveHotelRef = { name: string; htuKey: string; index: number | null };

const pageHotelsByChat = new Map<number, PageHotelRef[]>();
const activeHotelByChat = new Map<number, ActiveHotelRef>();

export const setPageHotelsForAssistant = (key: number, hotels: PageHotelRef[]) => {
  pageHotelsByChat.set(key, hotels);
};

export const getPageHotelsForAssistant = (key: number) => pageHotelsByChat.get(key) ?? [];

export const setActiveHotelForAssistant = (key: number, hotel: ActiveHotelRef | null) => {
  if (!hotel) {
    activeHotelByChat.delete(key);
    return;
  }
  activeHotelByChat.set(key, hotel);
};

export const getActiveHotelForAssistant = (key: number) => activeHotelByChat.get(key) ?? null;

const namesSpecificHotel = (text: string, hotels: PageHotelRef[]) => {
  const lower = text.toLowerCase();
  if (/#\s*\d+/.test(lower)) return true;
  if (/\b(?:first|1st|second|2nd|third|3rd|fourth|4th|fifth|5th)\s+hotel\b/.test(lower)) return true;
  return hotels.some((hotel) => {
    const name = hotel.name?.trim().toLowerCase();
    return !!name && name.length > 2 && lower.includes(name);
  });
};

const mentionsActiveHotel = (text: string) =>
  /\b(?:this|that|current|active|the)\s+hotel\b/i.test(text);

export type BareActiveHotelAction = {
  action: assistantService.HotelCardActionKind;
  pinned?: boolean | null;
  show?: boolean | null;
};

export const resolveBareActiveHotelAction = (
  text: string,
  hotels: PageHotelRef[],
  onHotelResults: boolean,
): BareActiveHotelAction | null => {
  const trimmed = text.trim();
  if (!trimmed || namesSpecificHotel(trimmed, hotels)) return null;

  const explicitHotel = mentionsActiveHotel(trimmed);
  const lower = trimmed.toLowerCase();
  const addHotel =
    /^(?:please\s+)?add(?:\s+(?:the|this|that|current|active))?\s+hotel\s+to\s+(?:the\s+)?proposal[.!?]?$/i.test(
      trimmed,
    ) ||
    (onHotelResults &&
      /^(?:please\s+)?add(?:\s+(?:it|this|that))?\s+to\s+(?:the\s+)?proposal[.!?]?$/i.test(trimmed));
  if (addHotel) return { action: 'add-to-proposal' };

  if (!explicitHotel && !onHotelResults) return null;

  if (/\bunpin\b/.test(lower)) return { action: 'pin', pinned: false };
  if (/\bpin\b/.test(lower)) return { action: 'pin', pinned: true };
  if (/\b(?:hide|close)\s+rooms\b/.test(lower)) return { action: 'show-rooms', show: false };
  if (/\b(?:show|open|see)\s+rooms\b/.test(lower)) return { action: 'show-rooms', show: true };
  if (/\b(?:refund|cancellation|cxl)\b/.test(lower)) return { action: 'open-refund' };
  if (/\b(?:click\s+price|open\s+price|book)\b/.test(lower)) return { action: 'click-price' };
  if (/\bpreferred\b/.test(lower)) return { action: 'open-preferred' };
  if (/\broom[-\s]?offers\b/.test(lower) || (explicitHotel && /\boffers\b/.test(lower))) {
    return { action: 'room-offers' };
  }
  if (/\bcenter\b/.test(lower)) return { action: 'center-me' };
  if (/\b(?:website|google)\b/.test(lower) && (explicitHotel || /\bit\b/.test(lower))) {
    return { action: 'www' };
  }
  if (/\bfocus\b/.test(lower) || /\bon\s+the\s+map\b/.test(lower)) return { action: 'focus' };
  if (/\bfilter\s+rooms\b/.test(lower) || /\broom\s+filter\b/.test(lower)) {
    return { action: 'filter-rooms' };
  }
  return null;
};

export const isBareAddHotelToProposal = (
  text: string,
  hotels: PageHotelRef[],
  onHotelResults: boolean,
) => resolveBareActiveHotelAction(text, hotels, onHotelResults)?.action === 'add-to-proposal';

export const annotateActiveHotelInText = (
  text: string,
  hotels: PageHotelRef[],
  active: ActiveHotelRef | null,
  onHotelResults: boolean,
) => {
  if (!active?.name || !resolveBareActiveHotelAction(text, hotels, onHotelResults)) return text;
  const position = active.index ? ` #${active.index}` : '';
  return `${text}. Use the active hotel${position} (${active.name}).`;
};

const ORDINAL_WORDS: Record<string, number> = {
  first: 0,
  '1st': 0,
  second: 1,
  '2nd': 1,
  third: 2,
  '3rd': 2,
  fourth: 3,
  '4th': 3,
  fifth: 4,
  '5th': 4,
};

export const resolveHotelOrdinalsInText = (text: string, hotels: PageHotelRef[]): string => {
  if (!text || !hotels.length) {
    return text;
  }

  let result = text.replace(/#(\d+)\b/g, (match, raw) => {
    const hotel = hotels[Number(raw) - 1];
    return hotel?.name ? `${match} (${hotel.name})` : match;
  });

  result = result.replace(
    /\b(first|1st|second|2nd|third|3rd|fourth|4th|fifth|5th)\s+hotel\b/gi,
    (match, word: string) => {
      const hotel = hotels[ORDINAL_WORDS[word.toLowerCase()]];
      const index = ORDINAL_WORDS[word.toLowerCase()];
      if (!hotel?.name || index == null) {
        return match;
      }
      return `${match} #${index + 1} (${hotel.name})`;
    },
  );

  return result;
};

// ---------------------------------------------------------------------------------------------------------------
// Flight-card clicks on their way to /flights/results (flight-card-action UI command).
// ---------------------------------------------------------------------------------------------------------------

export type PendingFlightCardAction = assistantService.FlightCardActionPayload;

const pendingFlightCardActions = new Map<number, PendingFlightCardAction[]>();
const flightCardActionListeners = new Set<() => void>();
let flightCardActionVersion = 0;

const notifyFlightCardActions = () => {
  flightCardActionVersion += 1;
  flightCardActionListeners.forEach((listener) => listener());
};

export const subscribeToPendingFlightCardActions = (listener: () => void) => {
  flightCardActionListeners.add(listener);
  return () => {
    flightCardActionListeners.delete(listener);
  };
};

export const getPendingFlightCardActionsVersion = () => flightCardActionVersion;

export const queueFlightCardAction = (key: number, action: PendingFlightCardAction) => {
  const queue = pendingFlightCardActions.get(key) ?? [];
  queue.push(action);
  pendingFlightCardActions.set(key, queue);
  notifyFlightCardActions();
};

export const peekPendingFlightCardAction = (key: number) => {
  const queue = pendingFlightCardActions.get(key);
  return queue?.[0] ?? null;
};

export const takePendingFlightCardAction = (key: number) => {
  const queue = pendingFlightCardActions.get(key);
  if (!queue?.length) {
    return null;
  }
  const action = queue.shift()!;
  if (queue.length) {
    pendingFlightCardActions.set(key, queue);
  } else {
    pendingFlightCardActions.delete(key);
  }
  notifyFlightCardActions();
  return action;
};

export const hasPendingFlightCardAction = (key: number) =>
  (pendingFlightCardActions.get(key)?.length ?? 0) > 0;

export type PageFlightRef = { id: string; label: string };

const pageFlightsByChat = new Map<number, PageFlightRef[]>();

export const setPageFlightsForAssistant = (key: number, flights: PageFlightRef[]) => {
  pageFlightsByChat.set(key, flights);
};

export const getPageFlightsForAssistant = (key: number) => pageFlightsByChat.get(key) ?? [];

export const resolveFlightOrdinalsInText = (text: string, flights: PageFlightRef[]): string => {
  if (!text || !flights.length) {
    return text;
  }

  let result = text.replace(/#(\d+)\b/g, (match, raw) => {
    const flight = flights[Number(raw) - 1];
    return flight?.label ? `${match} (${flight.label})` : match;
  });

  result = result.replace(
    /\b(first|1st|second|2nd|third|3rd|fourth|4th|fifth|5th)\s+flight\b/gi,
    (match, word: string) => {
      const index = ORDINAL_WORDS[word.toLowerCase()];
      const flight = flights[index];
      if (!flight?.label || index == null) {
        return match;
      }
      return `${match} #${index + 1} (${flight.label})`;
    },
  );

  result = result.replace(/\bflight\s+#?(\d+)\b/gi, (match, raw) => {
    const flight = flights[Number(raw) - 1];
    return flight?.label ? `${match} (${flight.label})` : match;
  });

  return result;
};

export type LocalFlightCardAction = {
  kind: 'info';
  flightId: string;
  show?: boolean | null;
  version: number;
};

let localFlightCardAction: LocalFlightCardAction | null = null;
const localFlightCardListeners = new Set<() => void>();

export const getLocalFlightCardAction = () => localFlightCardAction;

export const subscribeToLocalFlightCardAction = (listener: () => void) => {
  localFlightCardListeners.add(listener);
  return () => {
    localFlightCardListeners.delete(listener);
  };
};

export const publishLocalFlightCardAction = (
  action: Omit<LocalFlightCardAction, 'version'>,
) => {
  localFlightCardAction = { ...action, version: (localFlightCardAction?.version || 0) + 1 };
  localFlightCardListeners.forEach((listener) => listener());
};

export const clearLocalFlightCardAction = () => {
  if (!localFlightCardAction) {
    return;
  }
  localFlightCardAction = null;
  localFlightCardListeners.forEach((listener) => listener());
};

// Local card UI the page asked a HotelCard instance to open (preferred / refund / show-rooms).
export type LocalHotelCardAction = {
  kind: 'open-preferred' | 'open-refund' | 'show-rooms';
  hotelKey: string;
  roomKey?: string;
  show?: boolean | null;
  version: number;
};

let localCardAction: LocalHotelCardAction | null = null;
const localCardListeners = new Set<() => void>();

export const getLocalHotelCardAction = () => localCardAction;

export const subscribeToLocalHotelCardAction = (listener: () => void) => {
  localCardListeners.add(listener);
  return () => {
    localCardListeners.delete(listener);
  };
};

export const publishLocalHotelCardAction = (
  action: Omit<LocalHotelCardAction, 'version'>,
) => {
  localCardAction = { ...action, version: (localCardAction?.version || 0) + 1 };
  localCardListeners.forEach((listener) => listener());
};

export const clearLocalHotelCardAction = () => {
  if (!localCardAction) {
    return;
  }
  localCardAction = null;
  localCardListeners.forEach((listener) => listener());
};

// Rooms panel filter bar (RoomsFilter) patch from hotel-card-action filter-rooms.
export type LocalRoomFilterAction = {
  hotelKey: string;
  roomFilterName?: string | null;
  roomRefundableOnly?: boolean | null;
  roomAccessibleOnly?: boolean | null;
  roomBoardingOnly?: boolean | null;
  roomMealPlans?: string[] | null;
  roomSortByCxl?: boolean | null;
  clearRoomFilters?: boolean | null;
  version: number;
};

let localRoomFilterAction: LocalRoomFilterAction | null = null;
const localRoomFilterListeners = new Set<() => void>();

export const getLocalRoomFilterAction = () => localRoomFilterAction;

export const subscribeToLocalRoomFilterAction = (listener: () => void) => {
  localRoomFilterListeners.add(listener);
  return () => {
    localRoomFilterListeners.delete(listener);
  };
};

export const publishLocalRoomFilterAction = (
  action: Omit<LocalRoomFilterAction, 'version'>,
) => {
  localRoomFilterAction = {
    ...action,
    version: (localRoomFilterAction?.version || 0) + 1,
  };
  localRoomFilterListeners.forEach((listener) => listener());
};

export const clearLocalRoomFilterAction = () => {
  if (!localRoomFilterAction) {
    return;
  }
  localRoomFilterAction = null;
  localRoomFilterListeners.forEach((listener) => listener());
};

export const SET_RESULT_LOCATION = 'set-result-location';

export const NAVIGATE_PAGE = 'navigate-page';

export type NavigatePageKind = 'proposal' | 'my-orders' | 'flights' | 'hotels';

export const NAVIGATE_PAGE_PATHS: Record<NavigatePageKind, string> = {
  proposal: '/proposal',
  'my-orders': '/my-orders',
  flights: '/flights',
  hotels: '/hotels',
};

// ---------------------------------------------------------------------------------------------------------------
// Location toolbar fill from set-result-location (recenter existing hotels, not a new search).
// ---------------------------------------------------------------------------------------------------------------

export type PendingResultLocation = assistantService.SetResultLocationPayload;

const pendingResultLocations = new Map<number, PendingResultLocation>();
const resultLocationListeners = new Set<() => void>();
let resultLocationVersion = 0;

const notifyResultLocations = () => {
  resultLocationVersion += 1;
  resultLocationListeners.forEach((listener) => listener());
};

export const subscribeToPendingResultLocations = (listener: () => void) => {
  resultLocationListeners.add(listener);
  return () => {
    resultLocationListeners.delete(listener);
  };
};

export const getPendingResultLocationsVersion = () => resultLocationVersion;

export const queueResultLocation = (key: number, payload: PendingResultLocation) => {
  pendingResultLocations.set(key, payload);
  notifyResultLocations();
};

export const peekPendingResultLocation = (key: number) => pendingResultLocations.get(key) ?? null;

export const takePendingResultLocation = (key: number) => {
  const payload = pendingResultLocations.get(key) ?? null;
  pendingResultLocations.delete(key);
  if (payload) {
    notifyResultLocations();
  }
  return payload;
};

export const parseSetResultLocationPayload = (
  raw: Record<string, unknown> | null | undefined,
): PendingResultLocation | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  if (raw.clear === true) {
    return { clear: true };
  }
  const lat = typeof raw.lat === 'number' ? raw.lat : Number(raw.lat);
  const lng = typeof raw.lng === 'number' ? raw.lng : Number(raw.lng);
  const name = typeof raw.name === 'string' ? raw.name : '';
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !name) {
    return null;
  }
  const km = typeof raw.km === 'number' && raw.km > 0 ? raw.km : undefined;
  return { name, lat, lng, km: km ?? null };
};

// ---------------------------------------------------------------------------------------------------------------
// The floating widget: open or minimised, remembered for the browser tab.
// ---------------------------------------------------------------------------------------------------------------

const WIDGET_OPEN_STORAGE_KEY = 'assistantWidgetOpen';

const readWidgetOpen = () => {
  try {
    return sessionStorage.getItem(WIDGET_OPEN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

let widgetOpen = readWidgetOpen();
const widgetListeners = new Set<() => void>();

export const getWidgetOpen = () => widgetOpen;

export const setWidgetOpen = (open: boolean) => {
  if (open === widgetOpen) {
    return;
  }
  widgetOpen = open;
  try {
    sessionStorage.setItem(WIDGET_OPEN_STORAGE_KEY, open ? '1' : '0');
  } catch {
    // Remembering is a convenience; the widget works without it.
  }
  widgetListeners.forEach((listener) => listener());
};

export const subscribeToWidget = (listener: () => void) => {
  widgetListeners.add(listener);
  return () => {
    widgetListeners.delete(listener);
  };
};
